import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useEstimatedShippingArea } from "@/mutations/useEstimatedShippingArea";
import { useCart } from "@/queries/useCart";
import { IpregistryClient } from "@/services/IpregistryClient";
import { useNewCartStore } from "@/stores/useNewCartStore";
import type { Location } from "@/types/vendors/Ipregistry";
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
const STORAGE_KEY = "locationLastFetchedOn";

// Module-scoped fallback when sessionStorage is unavailable (private browsing,
// SSR). Mirrors the per-tab semantics of sessionStorage closely enough for our
// silent shipping-area estimation.
let inMemoryLastFetchedAt: number | null = null;
function readLastFetchedAt(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return inMemoryLastFetchedAt;
  }
}
function writeLastFetchedAt(timestamp: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(timestamp));
  } catch {
    inMemoryLastFetchedAt = timestamp;
  }
}

/**
 * Test-only: clear the in-memory fallback cache so per-test isolation works
 * even when sessionStorage is unavailable.
 */
export function __resetAutoEstimateShippingForTests(): void {
  inMemoryLastFetchedAt = null;
}
function hasShippableLocation(location: Location): boolean {
  return !!(location.city && location.country.name && location.region.code && location.postal);
}

/**
 * Silently estimates the shipping area on cart open via IP geolocation.
 * Mirrors the legacy CartHelper.fetchEstimatedLocation behavior, but uses
 * sessionStorage instead of localStorage so the cache is bounded to the tab.
 */
export function useAutoEstimateShipping() {
  const $ = _c(5);
  const cart = useCart();
  const estimateShipping = useEstimatedShippingArea();
  const isCartOpen = useNewCartStore(_temp);
  const isFetchingRef = useRef(false);
  const cartId = cart.data?.id;
  let t0;
  let t1;
  if ($[0] !== cartId || $[1] !== estimateShipping || $[2] !== isCartOpen) {
    t0 = () => {
      if (!isCartOpen) {
        return;
      }
      if (!cartId) {
        return;
      }
      if (isFetchingRef.current) {
        return;
      }
      const lastFetchedAt = readLastFetchedAt();
      if (lastFetchedAt && Date.now() - lastFetchedAt < SIX_HOURS_MS) {
        return;
      }
      isFetchingRef.current = true;
      IpregistryClient.lookupIp().then(response => {
        if (!hasShippableLocation(response.location)) {
          return;
        }
        writeLastFetchedAt(Date.now());
        estimateShipping.mutate(response.location);
      }).catch(_temp2).finally(() => {
        isFetchingRef.current = false;
      });
    };
    t1 = [isCartOpen, cartId, estimateShipping];
    $[0] = cartId;
    $[1] = estimateShipping;
    $[2] = isCartOpen;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function _temp2(error) {
  console.error("Failed to fetch estimated location", error);
}
function _temp(state) {
  return state.isCartOpen;
}